/** @format */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from 'react';

import {useMutation} from '@apollo/client';

import {VOTE_FOR_STUB_LAYER} from '../graphql/digitalCast.mutations';

export interface StubLayerData {
  layerId: string;
  totalVotes: number;
  hasVoted: boolean;
}

interface StubLayerContextValue {
  stubLayers: readonly StubLayerData[];
  getLayerData: (layerId: string) => StubLayerData | null;
  vote: (layerId: string) => Promise<void>;
}

const StubLayerContext = createContext<StubLayerContextValue | null>(null);

interface Props {
  stubLayers: readonly StubLayerData[];
  children: ReactNode;
}

export const StubLayerProvider = ({stubLayers: externalStubLayers, children}: Props) => {
  const [localStubLayers, setLocalStubLayers] =
    useState<readonly StubLayerData[]>(externalStubLayers);
  const [voteMutation] = useMutation(VOTE_FOR_STUB_LAYER);

  useEffect(() => {
    setLocalStubLayers(externalStubLayers);
  }, [externalStubLayers]);

  const getLayerData = useCallback(
    (layerId: string): StubLayerData | null => {
      const upper = layerId.toUpperCase();
      return localStubLayers.find(layer => layer.layerId.toUpperCase() === upper) ?? null;
    },
    [localStubLayers],
  );

  const vote = useCallback(
    async (layerId: string) => {
      const {data} = await voteMutation({
        variables: {layerId: layerId.toUpperCase()},
      });

      const voteResult = data?.voteForStubLayer;

      if (voteResult) {
        const upper = layerId.toUpperCase();
        setLocalStubLayers(prev =>
          prev.map(layer =>
            layer.layerId.toUpperCase() === upper
              ? {
                  ...layer,
                  totalVotes: voteResult.totalVotes,
                  hasVoted: voteResult.hasVoted,
                }
              : layer,
          ),
        );
      }
    },
    [voteMutation],
  );

  const value = useMemo(
    () => ({stubLayers: localStubLayers, getLayerData, vote}),
    [localStubLayers, getLayerData, vote],
  );

  return <StubLayerContext.Provider value={value}>{children}</StubLayerContext.Provider>;
};

export const useStubLayerContext = (): StubLayerContextValue => {
  const context = useContext(StubLayerContext);
  if (!context) {
    throw new Error('useStubLayerContext must be used within StubLayerProvider');
  }
  return context;
};
